import axios, { AxiosRequestConfig } from 'axios'
import { xAPIURL, isDebug } from '../App.config'
import { retrieveParamByName, getSessionInfo, getFromSession } from '../Utils'
import { Statement, LRSStatement, User, SessionInfo, ElectricalCode, FaultState, FullFaultAttempt, ScoreboardData } from '../Types'
import { lrsCSS } from '../components/learninglab/LearningLab'
import { SimutechModule, TS_Profile, ModuleProfile } from '../ModuleTypes'

export const xAPIcss = 'padding: 2px 4px; color: #f47e56; font-size: 12px; border-left: 2px solid purple'

enum EndPoints {
    QueryCompanyName = '/api/ccapi/QueryCompanyName',
    QueryLRSAgent = '/api/xapi/QueryLRSAgent',
    PostLRSStatement = '/api/xapi/PostLRSStatement',
    UpdateLRSAgent = '/api/xapi/UpdateLRSAgent',
    GetPartSpecs = '/api/rlu/GetPartSpecs',
    RunLRSQuery = '/api/xapi/RunLRSQuery',
}

export const getCompanyName = async (companyID: string) => {
    return await queryCompanyName(companyID)
        .then((data: any) => {
            console.log(`%cCompany Name: ${data.companyName}`, xAPIcss)
            return data.companyName
        })
        .catch((err) => {
            throw new Error(`Error retrieving company name for company ID ${companyID}: ${err}`)
        })
}

export const queryCompanyName = async <T extends unknown>(companyID: string): Promise<T> => {
    return await fetch(xAPIURL + EndPoints.QueryCompanyName + '?id=' + companyID)
        .then(response => {
            if (!response.ok) {
                throw new Error(response.statusText)
            }
            return response.json()
        }).catch(err => {
            throw new Error(`Error querying company name. ${err}`)
        })
}

export const getCompanyID = () => {
    const url = window.parent.location.href
    let val = retrieveParamByName('accountId', url)
    if (val === null) val = '12' // Need to throw an error if null and in Production.
    return val
}

export const getUserProfile = async <T extends unknown>(user: User): Promise<T> => {
    isDebug && console.log(`%cGetting user profile for:`, xAPIcss)
    isDebug && console.log(user)
    return await fetch(xAPIURL + EndPoints.QueryLRSAgent, {
        method: 'post',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(user)
    }).then((response) => {
        if (!response.ok) {
            throw new Error(response.statusText)
        }
        return response.json()
    }).catch(error => {
        throw new Error(`Error retrieving user profile. ${error}`)
    })
}

export const createStatement = (
    registrationId: string,
    action: string,
    learningGUID: string,
    duration?: string,
    sessionInfo?: SessionInfo
): Statement => {
    const statement: Statement = {
        'registration-id': registrationId,
        action: action,
        learningGUID: learningGUID,
        ...sessionInfo
    }
    if (duration !== undefined) {
        statement.duration = duration
    }

    return statement
}

export const sendInitialize = async (user: User, registrationID: string, learningGUID: string) => {
    try {
        const sessionInfo = getSessionInfo()
        const statement = createStatement(registrationID, 'initialize', learningGUID, undefined, sessionInfo)
        const lrsStatement: LRSStatement = { user, statement }
        await sendStatement(lrsStatement)
    }
    catch (error) {
        throw new Error(`Error sending initialize statement. ${error}`)
    }
}

const sendLRSBeacon = (statement: LRSStatement): boolean => {
    const url = xAPIURL + EndPoints.PostLRSStatement
    return navigator.sendBeacon(url, JSON.stringify(statement))
}

export const sendTerminateBeacon = (user: User, registrationID: string, learningGUID: string) => {
    try {
        isDebug && console.log('%cSending LRS Terminate beacon...', xAPIcss)
        const sessionTime = getSessionTime()
        const statement = createStatement(registrationID, 'terminate', learningGUID, sessionTime.toString())
        const lrsStatement: LRSStatement = { user, statement }
        sendLRSBeacon(lrsStatement)
    }
    catch (error) {
        throw new Error(`Error sending Terminate Beacon. ${error}`)
    }
}

const sendStatement = async (statement: LRSStatement) => {
    isDebug && console.log('%cSending LRS statement:', lrsCSS, statement)
    return await axios.post(xAPIURL + EndPoints.PostLRSStatement, JSON.stringify(statement), {
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(response => {
            return response.data
        })
        .catch(err => {
            const error = `Error sending LRS statement: ${err}`
            isDebug && console.error(error)
            throw new Error(error)
        })
}

export const updateLRSProfile = async <T extends unknown>(user: User, profile: Partial<T>): Promise<T> => {
    isDebug && console.log('%cUpdating LRS profile with: ', xAPIcss, profile)
    const updatedProfile = { user, profile }
    return await axios.post(xAPIURL + EndPoints.UpdateLRSAgent, JSON.stringify(updatedProfile), {
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(response => {
            return response.data
        })
        .catch(err => {
            const error = `Error updating LRS Profile: ${err}`
            isDebug && console.error(error)
            throw new Error(error)
        })
}

export const updateLRSModuleScore = async (user: User, moduleScore: number) => {
    const profile: Partial<ModuleProfile> = { 'module-score': moduleScore }
    await updateLRSProfile(user, profile)
}

export interface PartSpecsRequest {
    'moduleId': SimutechModule
    'partName': string
    'electricalCode': ElectricalCode
}

export const getPartSpecsImageURL = async (partSpecsRequest: PartSpecsRequest) => {
    isDebug && console.log('%cRetrieving Part Specs image...', xAPIcss, partSpecsRequest)
    return await axios.get(xAPIURL + EndPoints.GetPartSpecs + '?partData=' + JSON.stringify(partSpecsRequest),
        { responseType: 'blob' }
    )
        .then(response => URL.createObjectURL(new Blob([response.data])))
        .catch(err => {
            const error = `Error retrieving part specs image: ${err}`
            isDebug && console.error(error)
            throw new Error(error)
        })
}

// Fault History

export type FaultAttemptItem = {
    '_id': string
    'faultId': string
    'timestamp': string
    'faultState': FaultState
    'skillRating': null | number
}

enum Query {
    GetModuleSummaryByUser = 'GetModuleSummaryByUser',
    GetFaultAttemptSummaryByUser = 'GetFaultAttemptSummaryByUser',
    GetFaultAttemptListByUser = 'GetFaultAttemptListByUser',
    GetFaultAttemptDetails = 'GetFaultAttemptDetailsByFaultAttemptId'
}

type QueryParameters = Record<string, string>
interface QueryRequest extends AxiosRequestConfig {
    companyId: string
    queryName: string
    queryParms?: QueryParameters
}

const runLRSQuery = async <T extends unknown>(queryName: Query, companyId: string, queryParams?: QueryParameters): Promise<T> => {
    const endPoint = EndPoints.RunLRSQuery
    const url = xAPIURL + endPoint
    const request: QueryRequest = {
        'companyId': companyId,
        'queryName': queryName,
        'queryParms': {
            ...queryParams
        }
    }
    isDebug && console.log(`%c${endPoint} query REQUEST: ${request.queryName}`, xAPIcss, request)

    return await axios.post(url, request, { headers: { 'Content-Type': 'application/json' } })
        .then(response => {
            isDebug && console.log(`%c${endPoint} query RESPONSE: ${request.queryName}`, xAPIcss, response)
            return response.data.queryResults as T
        })
        .catch(error => {
            console.error(`An error occurred while retrieving ${endPoint} data:`, error)
            console.error(`The error occurred with query request:`, request)
            throw new Error(`An error occurred while retrieving ${endPoint} data: ${error}`)
        })
}


export const getFaultAttemptListByUser = async (userId: string, companyId: string, moduleId: SimutechModule): Promise<FaultAttemptItem[]> => {
    const params: QueryParameters = { 'userId': userId, 'moduleId': moduleId }
    return await runLRSQuery<FaultAttemptItem[]>(Query.GetFaultAttemptListByUser, companyId, params)
}

export const getFaultAttemptDetails = async (attemptId: string, companyId: string): Promise<FullFaultAttempt[]> => {
    const params: QueryParameters = { faultAttemptId: attemptId }
    return await runLRSQuery<FullFaultAttempt[]>(Query.GetFaultAttemptDetails, companyId, params)
}

export const getScoreboardValues = async (user: User) => {
    const userProfile = await getUserProfile(user)
        .then((result: any) => JSON.parse(result).profile as TS_Profile)

    const scoreboardValues: ScoreboardData = {
        overallScore: {
            name: 'Overall Score',
            description: 'The more you successfully solve faults, the higher your score will go.',
            value: userProfile['module-overall-score']
        },
        levelAchieved: {
            name: 'Achievements',
            description: 'Successfully complete four faults in a level with an average skill rating above the level target (60 - 80%) to move up to the next achievement level.',
            value: userProfile['module-level-achieved']
        },
        faultsAttempted: userProfile['module-number-faults-attempted'],
        faultsSolved: userProfile['module-number-faults-solved'],
        faultPercentage: Math.round(userProfile['module-percentage-faults-solved'] * 100),
        accuracyScore: {
            name: 'Accuracy',
            description: 'Achieve a high accuracy rating (100%) by replacing only defective parts and not more than necessary.',
            value: Math.round(userProfile['module-accuracy-score'] * 100)
        },
        efficiencyScore: {
            name: 'Efficiency',
            description: 'Achieve a high efficiency rating (up to 120%) by completing faults within the allocated time and following the Simutech Troubleshooting Approach.',
            value: Math.round(userProfile['module-efficiency-score'] * 100)
        },
        safetyScore: {
            name: 'Safety',
            description: 'Achieve a high safety rating (100%) by following safety procedures and avoiding safety errors and warnings.',
            value: Math.round(userProfile['module-safety-score'] * 100)
        },
        skillRating: {
            name: 'Skill Rating',
            description: 'Achieve a higher overall skill rating (up to 120%) by improving in the other three ratings.',
            value: Math.round(userProfile['module-skill-rating'] * 100)
        },
    }
    return scoreboardValues
}

const getSessionTime = () => {
    const sessionEnd = new Date().getTime()
    const sessionStart = Number(getFromSession('sessionStart'))
    if (!sessionStart)
        throw new Error('No sessionStart timestamp found in session.')
    const sessionTime = (sessionEnd - sessionStart)
    isDebug && console.log('Total session time:', sessionTime)
    return sessionTime
}
